import { useCallback, useEffect, useMemo, useState } from "react";
import { Target, Download, CalendarRange, Megaphone, CheckCircle2, XCircle, Loader2, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import DepartmentTargetsPanel from "@/components/DepartmentTargetsPanel";
import WeekSubmitPanel from "@/components/WeekSubmitPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { todayIstIso } from "@/utils/date";

const fmt = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const mondayOf = (iso) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function WeekChips({ weeks }) {
  return (
    <div className="grid gap-1 mt-2" style={{ gridTemplateColumns: `repeat(${weeks.length || 1}, minmax(0, 1fr))` }}>
      {weeks.map((w) => (
        <div key={w.start} title={`${fmt(w.start)}–${fmt(w.end)} · target ${w.target}, done ${w.done}`}
          className={`rounded px-1.5 py-1 text-center border text-xs ${w.target > 0 && w.done >= w.target ? "bg-emerald-500/10 border-emerald-500/40" : w.done > 0 ? "bg-yellow-500/10 border-yellow-500/40" : "bg-white/5 border-white/10 text-white/60"}`}>
          <div className="text-[0.7rem] opacity-70">W{w.index}</div>
          <div className="font-medium">{w.done}<span className="opacity-50">/{w.target}</span></div>
        </div>
      ))}
    </div>
  );
}

function SocialTargets({ month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/targets/social", { params: { month } })
      .then(({ data: d }) => alive && setData(d))
      .catch(() => alive && setData({ rows: [], allowed: false }));
    return () => { alive = false; };
  }, [month]);
  if (!data || !data.allowed) return null;
  return (
    <section className="glass rounded-xl p-4" data-testid="social-targets">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone size={16} className="text-[#E11D2E]" />
        <h2 className="text-base font-semibold">Social calendar targets</h2>
        <span className="text-xs text-white/50">done = posts marked complete</span>
      </div>
      {data.rows.length === 0 ? <div className="text-sm text-white/50">No client targets set in the Social Calendar.</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {data.rows.map((r) => {
            const pct = Math.min(100, Math.round((r.done / r.monthly_target) * 100));
            return (
              <div key={`${r.client}-${r.post_type}`} className="rounded-md border border-white/10 p-3" data-testid={`social-target-${r.client}-${r.post_type}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0"><div className="text-sm font-semibold truncate">{r.client}</div><div className="text-xs text-white/55">{r.label}</div></div>
                  <div className="text-lg font-display">{r.done}<span className="text-white/40">/{r.monthly_target}</span></div>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 mt-2 overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#E11D2E]"}`} style={{ width: `${pct}%` }} /></div>
                <WeekChips weeks={r.weeks} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const KIND = { team: "Department", individual: "Individual", social: "Social calendar" };

function WeeklyReport() {
  const weeks = useMemo(() => {
    const cur = mondayOf(todayIstIso());
    return Array.from({ length: 12 }, (_, i) => { const d = new Date(cur); d.setDate(d.getDate() - 7 * i); return isoOf(d); });
  }, []);
  const [week, setWeek] = useState(weeks[1]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setData(null);
    try { const { data: d } = await api.get("/targets/weekly-report", { params: { week_start: week } }); setData(d); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load report"); }
  }, [week]);
  useEffect(() => { load(); }, [load]);

  const pdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/targets/weekly-report/pdf", { params: { week_start: week }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `LIM-Weekly-Targets-${week}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); } finally { setBusy(false); }
  };

  const sm = data?.summary;
  return (
    <section data-testid="weekly-target-report">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm font-medium">Week
          <select value={week} onChange={(e) => setWeek(e.target.value)} data-testid="weekly-report-week"
            className="block mt-1 h-10 rounded-md border border-white/15 bg-surface px-3 text-sm">
            {weeks.map((w, i) => {
              const end = new Date(`${w}T00:00:00`); end.setDate(end.getDate() + 6);
              return <option key={w} value={w}>{fmt(w)} – {fmt(isoOf(end))}{i === 0 ? " (this week)" : i === 1 ? " (last week)" : ""}</option>;
            })}
          </select>
        </label>
        <Button onClick={pdf} disabled={busy || !data} data-testid="weekly-report-pdf" className="h-10 bg-[#E11D2E] hover:bg-[#F03041] text-white">
          {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Download size={15} className="mr-1.5" />} Download PDF
        </Button>
        {data?.generated_at && <span className="text-xs text-white/50 pb-3">Report generated {new Date(data.generated_at).toLocaleString("en-IN")}</span>}
      </div>
      {!data ? <div className="text-sm text-white/50">Loading report…</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[["Week target", sm.target, ""], ["Done", sm.done, "text-emerald-600 dark:text-emerald-400"], ["Missed", sm.missed, "text-red-600 dark:text-red-400"], ["Hit rate", sm.hit_rate == null ? "—" : `${sm.hit_rate}%`, ""]].map(([l, v, c]) => (
              <div key={l} className="glass rounded-xl p-4" data-testid={`weekly-report-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`text-2xl font-display ${c}`}>{v}</div><div className="text-xs text-white/55 mt-1">{l}</div>
              </div>
            ))}
          </div>
          {data.rows.length === 0 ? <div className="glass rounded-xl p-6 text-sm text-white/50 text-center">No targets fell in this week.</div> : (
            <div className="glass rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead><tr className="border-b border-white/10 text-left text-xs text-white/55">
                  {["Type", "Department", "Person", "Target", "Week target", "Done", "Missed", "Month progress"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`weekly-report-row-${i}`}>
                      <td className="px-3 py-2 text-white/70">{KIND[r.kind]}</td>
                      <td className="px-3 py-2">{r.team}</td>
                      <td className="px-3 py-2">{r.assignee_name || "—"}</td>
                      <td className="px-3 py-2 font-semibold">{r.name}</td>
                      <td className="px-3 py-2">{r.target}</td>
                      <td className="px-3 py-2">{r.done}</td>
                      <td className="px-3 py-2">
                        {r.missed > 0
                          ? <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"><XCircle size={13} /> {r.missed}</span>
                          : <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={13} /> Met</span>}
                      </td>
                      <td className="px-3 py-2 text-white/70">{r.month_done} / {r.month_target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}


function MonthlyReportTab() {
  const [month, setMonth] = useState(todayIstIso().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/targets/monthly-report", { params: { month } })
      .then(({ data: d }) => alive && setData(d))
      .catch((e) => { if (!alive) return; setData({ error: true }); toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load report"); });
    return () => { alive = false; };
  }, [month]);
  const pdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/targets/monthly-report/pdf", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `LIM-Monthly-Targets-${month}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); } finally { setBusy(false); }
  };
  const sm = data?.summary;
  return (
    <section data-testid="monthly-target-report">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-sm font-medium">Month
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="monthly-report-month-picker"
            className="mt-1 h-10 w-44 bg-surface border-white/15" />
        </label>
        <Button onClick={pdf} disabled={busy || !data || data.error} data-testid="monthly-target-report-pdf" className="h-10 bg-[#E11D2E] hover:bg-[#F03041] text-white">
          {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Download size={15} className="mr-1.5" />} Download PDF
        </Button>
        {data?.generated_at && <span className="text-xs text-white/50 pb-3">Report generated {new Date(data.generated_at).toLocaleString("en-IN")}</span>}
      </div>
      {!data ? <div className="text-sm text-white/50">Loading report…</div> : data.error ? <div className="text-sm text-red-600" data-testid="monthly-target-report-error">Couldn't load this report. Try another month or refresh.</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            {[["Month target", sm.target, ""], ["Done", sm.done, "text-emerald-600 dark:text-emerald-400"], ["Missed", sm.missed, "text-red-600 dark:text-red-400"],
              ["Targets met", `${sm.met}/${sm.count}`, ""], ["Hit rate", sm.hit_rate == null ? "—" : `${sm.hit_rate}%`, ""]].map(([l, v, c]) => (
              <div key={l} className="glass rounded-xl p-4" data-testid={`monthly-target-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`text-2xl font-display ${c}`}>{v}</div><div className="text-xs text-white/55 mt-1">{l}</div>
              </div>
            ))}
          </div>
          {data.rows.length === 0 ? <div className="glass rounded-xl p-6 text-sm text-white/50 text-center">No targets set for this month.</div> : (
            <div className="glass rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[980px]">
                <thead><tr className="border-b border-white/10 text-left text-xs text-white/55">
                  {["Type", "Department", "Person", "Target"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
                  {data.weeks.map((w, i) => <th key={w.start} className="px-2 py-2.5 font-medium text-center" title={`${fmt(w.start)}–${fmt(w.end)} · ${w.working_days} working days`}>W{i + 1}<div className="font-normal opacity-70">{w.start.slice(8)}–{w.end.slice(8)}</div></th>)}
                  {["Month", "Done", "Missed", "%"].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`monthly-target-row-${i}`}>
                      <td className="px-3 py-2 text-white/70">{KIND[r.kind]}</td>
                      <td className="px-3 py-2">{r.team}</td>
                      <td className="px-3 py-2">{r.assignee_name || "—"}</td>
                      <td className="px-3 py-2 font-semibold">{r.name}</td>
                      {r.weeks.map((w) => (
                        <td key={w.start} className="px-2 py-2 text-center">
                          <span className={`inline-block min-w-[3rem] rounded px-1.5 py-0.5 text-xs ${w.target === 0 ? "text-white/40" : w.done >= w.target ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-red-500/10 text-red-700 dark:text-red-300"}`}>{w.done}/{w.target}</span>
                        </td>
                      ))}
                      <td className="px-3 py-2">{r.target}</td>
                      <td className="px-3 py-2">{r.done}</td>
                      <td className="px-3 py-2">{r.missed > 0 ? <span className="text-red-600 dark:text-red-400">{r.missed}</span> : <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={13} /> Met</span>}</td>
                      <td className="px-3 py-2 font-semibold">{r.pct == null ? "—" : `${r.pct}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function MonthlyTargets() {
  const { user } = useAuth();
  const [tab, setTab] = useState("targets");
  const [month, setMonth] = useState(todayIstIso().slice(0, 7));
  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="monthly-targets-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2"><Target size={13} /> Monthly Targets</div>
          <h1 className="font-display mt-1">Targets & weekly tracking</h1>
          <p className="text-sm text-white/55 mt-1">Department and individual targets, split into weeks by working days (Mon–Sat). Targets carry into next month until changed.</p>
        </div>
        {tab === "targets" && (
          <label className="text-sm font-medium">Month
            <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="monthly-targets-month" className="mt-1 h-10 w-44 bg-surface border-white/15" />
          </label>
        )}
      </div>
      <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-white/10 p-1 bg-surface" role="tablist">
        {[["targets", "Targets", Target], ["weekly", "Weekly reports", CalendarRange], ["monthly", "Monthly reports", FileBarChart]].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`monthly-targets-tab-${k}`}
            className={`h-8 px-2.5 sm:px-3 whitespace-nowrap rounded text-sm inline-flex items-center gap-1.5 transition-colors ${tab === k ? "bg-[#E11D2E]/10 text-[#E11D2E] font-semibold" : "text-white/60 hover:text-white"}`}>
            <I size={14} /> {l}
          </button>
        ))}
      </div>
      {tab === "targets" ? (
        <>
          <WeekSubmitPanel />
          <DepartmentTargetsPanel currentUser={user} month={month} defaultOpen />
          <SocialTargets month={month} />
        </>
      ) : tab === "weekly" ? <WeeklyReport /> : <MonthlyReportTab />}
    </div>
  );
}
